import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Callout } from '../ui';
import { LocationData } from '../LocationSelector';
import { ContactFormFields } from './ContactFormFields';

// ── Schema ────────────────────────────────────────────────────────────────────
const contactSchema = z.object({
  nickname: z
    .string()
    .min(1, 'Nickname is required')
    .min(2, 'Nickname must be at least 2 characters')
    .max(50, 'Nickname must be less than 50 characters'),
  contactName: z
    .string()
    .min(1, 'Contact name is required')
    .min(2, 'Contact name must be at least 2 characters')
    .max(100, 'Contact name must be less than 100 characters')
    .regex(/^[a-zA-Z\s\u0600-\u06FF\u0590-\u05FF'-]+$/, 'Contact name contains invalid characters'),
  companyName: z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 2, 'Company name must be at least 2 characters')
    .refine((val) => !val || val.length <= 100, 'Company name must be less than 100 characters'),
  email: z
    .string()
    .min(1, 'Email address is required')
    .email('Please enter a valid email address')
    .max(254, 'Email address is too long')
    .toLowerCase(),
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .min(8, 'Phone number must be at least 8 digits')
    .max(20, 'Phone number is too long')
    .regex(/^[+]?[\d\s\-()]{8,20}$/, 'Please enter a valid phone number'),
  street: z
    .string()
    .min(1, 'Street address is required')
    .min(3, 'Street address must be at least 3 characters')
    .max(200, 'Street address is too long'),
  houseNo: z
    .string()
    .optional()
    .refine((val) => !val || val.length <= 20, 'House number is too long'),
  zipCode: z
    .string()
    .min(1, 'Zip code is required')
    .max(20, 'Zip code is too long')
    .regex(/^[A-Za-z0-9\s-]{1,20}$/, 'Invalid zip code format'),
  isFavorite: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  deliveryDetails: z
    .string()
    .optional()
    .refine((val) => !val || val.length <= 500, 'Delivery details are too long'),
  country_id: z
    .number()
    .nullable()
    .refine((val) => val !== null && val > 0, 'Please select a country'),
  state_id: z.number().nullable().optional(),
  city_id: z.number().nullable().optional(),
  city_other: z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 2, 'City name must be at least 2 characters')
    .refine((val) => !val || val.length <= 100, 'City name is too long'),
}).refine(
  (data) => data.city_id || (data.city_other && data.city_other.trim().length > 0),
  { message: 'Please select or enter a valid city', path: ['city_id'] },
);

// ── Exported types ────────────────────────────────────────────────────────────
export type ContactFormValues = z.infer<typeof contactSchema>;

export interface Contact {
  id?: string;
  userId?: string;
  nickname?: string;
  contactName: string;
  companyName?: string;
  email: string;
  phone: string;
  /** Sender national/tax ID — appears on shipping labels for the sender party. */
  nationalId?: string;
  country?: string | any;
  city?: string | any;
  state?: string | any;
  street: string;
  houseNo?: string;
  zipCode?: string;
  deliveryDetails?: string;
  createdAt?: string;
  updatedAt?: string;
  isTemporary?: boolean;
  isFavorite?: boolean;
  isDefault?: boolean;
  country_id?: number | null;
  state_id?: number | null;
  city_id?: number | null;
  city_other?: string;
  countryCode?: string;
  stateCode?: string;
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface ContactFormProps {
  isEditMode?: boolean;
  initialData?: Partial<Contact>;
  showTemporaryOption?: boolean;
  useTemporaryAddress?: boolean;
  onTemporaryToggle?: (value: boolean) => void;
  onSubmit: (data: ContactFormValues) => void;
  isSubmitting?: boolean;
  formError?: string | null;
  isRTL?: boolean;
  lockedCountryId?: number | null;
  lockedCountryName?: string;
  lockedCountryIso2?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────
const ContactForm: React.FC<ContactFormProps> = ({
  isEditMode = false,
  initialData,
  showTemporaryOption = false,
  useTemporaryAddress = false,
  onTemporaryToggle,
  onSubmit,
  isSubmitting = false,
  formError,
  isRTL = false,
  lockedCountryId,
  lockedCountryName,
  lockedCountryIso2,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, touchedFields },
    setValue,
    watch,
    trigger,
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      nickname: '',
      contactName: '',
      companyName: '',
      email: '',
      phone: '',
      street: '',
      houseNo: '',
      zipCode: '',
      isFavorite: false,
      isDefault: false,
      deliveryDetails: '',
      country_id: null,
      state_id: null,
      city_id: null,
      city_other: '',
    },
  });

  // Populate form when initialData changes
  useEffect(() => {
    if (initialData) {
      setValue('nickname', initialData.nickname || '');
      setValue('contactName', initialData.contactName || '');
      setValue('companyName', initialData.companyName || '');
      setValue('email', initialData.email || '');
      setValue('phone', initialData.phone || '');
      setValue('street', initialData.street || '');
      setValue('houseNo', initialData.houseNo || '');
      setValue('zipCode', initialData.zipCode || '');
      setValue('deliveryDetails', initialData.deliveryDetails || '');
      setValue('isFavorite', !!initialData.isFavorite);
      setValue('isDefault', !!initialData.isDefault);
      setValue('country_id', initialData.country_id || null);
      setValue('state_id', initialData.state_id || null);
      setValue('city_id', initialData.city_id || null);
      setValue('city_other', initialData.city_other || '');
    }
  }, [initialData, setValue]);

  // Enforce lockedCountryId when set
  const currentCountryId = watch('country_id');
  useEffect(() => {
    if (!lockedCountryId || currentCountryId === lockedCountryId) return;
    setValue('country_id', lockedCountryId, { shouldDirty: false, shouldValidate: true });
  }, [currentCountryId, lockedCountryId, setValue]);

  // Location handlers
  const handleLocationChange = (locationData: LocationData) => {
    setValue('country_id', locationData.country_id === null ? null : Number(locationData.country_id));
    setValue('state_id', locationData.state_id === null ? null : Number(locationData.state_id));
    setValue('city_id', locationData.city_id === null ? null : Number(locationData.city_id));
    setValue('city_other', locationData.city_other || '');
  };

  const handleGooglePlaceSelect = (locationData: any) => {
    if (locationData.country_id !== undefined) setValue('country_id', locationData.country_id);
    if (locationData.state_id !== undefined) setValue('state_id', locationData.state_id);
    if (locationData.city_id !== undefined) setValue('city_id', locationData.city_id);
    if (locationData.city_other !== undefined) setValue('city_other', locationData.city_other);
    if (locationData.street) setValue('street', locationData.street);
  };

  const onFormSubmit = async (data: ContactFormValues) => {
    try {
      const isFormValid = await trigger();
      if (!isFormValid) {
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
          const el = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
          el?.focus();
        }
        return;
      }
      onSubmit(data);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div>
      {formError && (
        <Callout tone="warn" style={{ marginBottom: 20, fontSize: 13 }}>
          <strong>Error:</strong> {formError}
        </Callout>
      )}

      <form id="contact-form" onSubmit={handleSubmit(onFormSubmit)} autoComplete="off">
        <ContactFormFields
          register={register}
          control={control}
          errors={errors}
          touchedFields={touchedFields}
          watch={watch}
          trigger={trigger}
          isRTL={isRTL}
          isEditMode={isEditMode}
          showTemporaryOption={showTemporaryOption}
          useTemporaryAddress={useTemporaryAddress}
          onTemporaryToggle={onTemporaryToggle}
          lockedCountryId={lockedCountryId}
          lockedCountryName={lockedCountryName}
          lockedCountryIso2={lockedCountryIso2}
          onLocationChange={handleLocationChange}
          onGooglePlaceSelect={handleGooglePlaceSelect}
        />
      </form>
    </div>
  );
};

export default ContactForm;
